import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../inventory/components/Navbar';
import Footer from '../home/components/Footer';

interface WishlistVehicle {
  id: number;
  name: string;
  price: number;
  mileage: string;
  location: string;
  badge?: string;
  images: string[];
  addedDate: string;
}

export default function WishlistPage() {
  const navigate = useNavigate();
  const [wishlistVehicles, setWishlistVehicles] = useState<WishlistVehicle[]>([]);
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    loadWishlist();
  }, []);

  const loadWishlist = () => {
    const saved = localStorage.getItem('vehicle-wishlist');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setWishlistVehicles(parsed);
      } catch (error) {
        console.error('Error loading wishlist:', error);
      }
    }
  };

  const removeFromWishlist = (vehicleId: number) => {
    const updated = wishlistVehicles.filter(v => v.id !== vehicleId);
    setWishlistVehicles(updated);
    localStorage.setItem('vehicle-wishlist', JSON.stringify(updated));
    
    // Dispatch event to update other components
    window.dispatchEvent(new CustomEvent('wishlist-updated'));
  };

  const clearWishlist = () => {
    if (window.confirm('Are you sure you want to clear your entire wishlist?')) {
      setWishlistVehicles([]);
      localStorage.removeItem('vehicle-wishlist');
      window.dispatchEvent(new CustomEvent('wishlist-updated'));
    }
  };

  const handleViewDetails = (vehicle: WishlistVehicle) => {
    navigate('/vehicle-details', { state: { vehicle } });
  };

  const getSortedVehicles = () => {
    const sorted = [...wishlistVehicles];
    switch (sortBy) {
      case 'recent':
        return sorted.sort((a, b) => new Date(b.addedDate).getTime() - new Date(a.addedDate).getTime());
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return sorted;
    }
  };

  const sortedVehicles = getSortedVehicles();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">My Wishlist</h1>
              <p className="text-gray-600">
                {wishlistVehicles.length === 0 
                  ? 'You haven\'t saved any vehicles yet' 
                  : `${wishlistVehicles.length} vehicle${wishlistVehicles.length !== 1 ? 's' : ''} saved`}
              </p>
            </div>
            {wishlistVehicles.length > 0 && (
              <button
                onClick={clearWishlist}
                className="px-6 py-3 bg-red-50 text-red-600 rounded-full font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-delete-bin-line"></i>
                Clear All
              </button>
            )}
          </div>

          {wishlistVehicles.length > 0 && (
            <div className="flex items-center gap-4 bg-[#F8F9FA] rounded-2xl p-4">
              <label className="text-sm text-gray-600 font-medium">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              >
                <option value="recent">Recently Added</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name">Name: A to Z</option>
              </select>
            </div>
          )}
        </div>

        {/* Empty State */}
        {wishlistVehicles.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-heart-line text-5xl text-gray-400"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Your wishlist is empty</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Start adding vehicles to your wishlist by clicking the heart icon on any vehicle you're interested in
            </p>
            <button
              onClick={() => navigate('/inventory')}
              className="px-8 py-4 bg-[#2563EB] text-white rounded-full font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-car-line"></i>
              Browse Inventory
            </button>
          </div>
        ) : (
          /* Vehicle Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedVehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative w-full h-64 bg-gray-100">
                  <img
                    src={vehicle.images[0]}
                    alt={vehicle.name}
                    className="w-full h-full object-cover object-top cursor-pointer"
                    onClick={() => handleViewDetails(vehicle)}
                  />
                  {vehicle.badge && (
                    <div className="absolute top-4 left-4 bg-[#2563EB] text-white px-4 py-1.5 rounded-full text-xs font-medium">
                      {vehicle.badge}
                    </div>
                  )}
                  <button
                    onClick={() => removeFromWishlist(vehicle.id)}
                    className="absolute top-4 right-4 w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer shadow-lg"
                    title="Remove from wishlist"
                  >
                    <i className="ri-heart-fill text-xl"></i>
                  </button>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{vehicle.name}</h3>
                  <p className="text-sm text-gray-500 mb-1">
                    {vehicle.mileage} miles • {vehicle.location}
                  </p>
                  <p className="text-xs text-gray-400 mb-4">
                    Added {new Date(vehicle.addedDate).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mb-6">
                    ${vehicle.price.toLocaleString()}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleViewDetails(vehicle)}
                      className="flex-1 py-3 bg-[#2563EB] text-white rounded-full font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => removeFromWishlist(vehicle.id)}
                      className="w-12 h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
                      title="Remove"
                    >
                      <i className="ri-close-line text-xl text-gray-600"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        {wishlistVehicles.length > 0 && (
          <div className="mt-12 bg-gradient-to-br from-[#2563EB] to-[#1d4ed8] rounded-3xl p-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Ready to take the next step?</h3>
                <p className="text-white/90">
                  Schedule test drives or get financing pre-approval for your favorite vehicles
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/inventory')}
                  className="px-6 py-3 bg-white text-[#2563EB] rounded-full font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Browse More
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
